import { createStyles, makeStyles, Theme } from "@material-ui/core";

const usePixModalStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Dialog
    dialogPaper: {
      borderRadius: 16,
      overflow: "hidden",
      maxWidth: 480,
      width: "100%",
      margin: 16,
      padding: 0,
      maxHeight: "calc(100vh - 32px)",
      display: "flex",
      flexDirection: "column" as const,
      [theme.breakpoints.down("xs")]: {
        margin: 8,
        maxHeight: "calc(100vh - 16px)",
        borderRadius: 12,
      },
    },
    // Header
    header: {
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    headerPending: {
      background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
      borderTop: "4px solid #D91F05",
    },
    headerConfirmed: {
      background: "linear-gradient(135deg, #F0FDF4, #FFFFFF)",
      borderTop: "4px solid #22C55E",
    },
    headerIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      flexShrink: 0,
    },
    headerIconPending: {
      background: "#FEE2E2",
      color: "#D91F05",
    },
    headerIconConfirmed: {
      background: "#DCFCE7",
      color: "#16A34A",
    },
    headerTextWrapper: {
      flex: 1,
      minWidth: 0,
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#1F2937",
      margin: 0,
      lineHeight: 1.3,
    },
    headerSubtitle: {
      fontSize: 12,
      fontWeight: 400,
      color: "#6B7280",
      margin: 0,
    },
    badge: {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 10,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    badgePending: {
      background: "#FEE2E2",
      color: "#DC2626",
    },
    badgeConfirmed: {
      background: "#DCFCE7",
      color: "#16A34A",
    },
    closeButton: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      border: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: "#fff",
      color: "#6B7280",
      fontSize: 14,
      flexShrink: 0,
      "&:hover": {
        background: "#F3F4F6",
      },
    },
    // Body
    body: {
      padding: 20,
      background: "#fff",
      overflowY: "auto" as const,
      overflowX: "hidden" as const,
      flex: 1,
      minHeight: 0,
    },
    // Order Card
    orderCard: {
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: "14px 16px",
      marginBottom: 16,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    orderCardConfirmed: {
      borderColor: "#DCFCE7",
    },
    orderInfo: {
      flex: 1,
      minWidth: 0,
    },
    orderNumber: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1F2937",
      margin: "0 0 4px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    orderStatusBadge: {
      fontSize: 9,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 4,
      textTransform: "uppercase" as const,
    },
    orderStatusBlocked: {
      background: "#FEE2E2",
      color: "#DC2626",
    },
    orderStatusReleased: {
      background: "#DCFCE7",
      color: "#16A34A",
    },
    orderCustomer: {
      fontSize: 13,
      fontWeight: 600,
      color: "#1F2937",
      margin: "2px 0",
    },
    orderProduct: {
      fontSize: 12,
      color: "#6B7280",
      margin: "2px 0",
    },
    orderVolume: {
      fontSize: 13,
      fontWeight: 600,
      margin: "2px 0",
    },
    orderVolumePending: {
      color: "#D91F05",
    },
    orderVolumeConfirmed: {
      color: "#16A34A",
    },
    orderDateBox: {
      textAlign: "center" as const,
      flexShrink: 0,
    },
    orderDateIcon: {
      width: 32,
      height: 32,
      border: "1px solid #E5E7EB",
      borderRadius: 6,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 4px",
    },
    orderDateIconPending: {
      borderColor: "#D91F05",
      color: "#D91F05",
    },
    orderDateIconConfirmed: {
      borderColor: "#22C55E",
      color: "#22C55E",
    },
    orderDateLabel: {
      fontSize: 10,
      color: "#6B7280",
    },
    orderDateValue: {
      fontSize: 12,
      fontWeight: 700,
      color: "#1F2937",
    },
    // QR Code Section
    qrSection: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 16,
      marginBottom: 16,
      minWidth: 0,
      overflow: "hidden",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
      },
    },
    qrBox: {
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: 16,
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      overflow: "hidden",
      minWidth: 0,
    },
    qrCanvas: {
      marginBottom: 10,
    },
    qrCodeText: {
      background: "#F3F4F6",
      border: "1px dashed #D1D5DB",
      borderRadius: 6,
      padding: "6px 10px",
      fontSize: 11,
      fontFamily: "monospace",
      color: "#6B7280",
      width: "100%",
      textAlign: "center" as const,
      overflowWrap: "anywhere" as const,
      wordBreak: "break-all" as const,
      whiteSpace: "normal" as const,
      overflow: "hidden",
      maxWidth: "100%",
      boxSizing: "border-box" as const,
      margin: "8px 0",
    },
    btnCopy: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 14px",
      border: "1px solid #E5E7EB",
      borderRadius: 8,
      background: "#fff",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      color: "#1F2937",
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      "&:hover": {
        background: "#F9FAFB",
      },
    },
    // Value Section
    valueSection: {
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      padding: 16,
    },
    valueLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: "#6B7280",
      textTransform: "uppercase" as const,
      letterSpacing: 0.5,
      marginBottom: 4,
    },
    valueAmount: {
      fontSize: 32,
      fontWeight: 700,
      color: "#1F2937",
      lineHeight: 1.2,
      marginBottom: 12,
    },
    valueCurrency: {
      fontSize: 16,
      color: "#6B7280",
    },
    valueDecimals: {
      fontSize: 16,
    },
    // Instructions
    instructions: {
      marginTop: 12,
    },
    instructionItem: {
      display: "flex",
      gap: 10,
      margin: "8px 0",
      fontSize: 12,
      color: "#4B5563",
      lineHeight: 1.5,
    },
    instructionNum: {
      width: 20,
      height: 20,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 10,
      fontWeight: 700,
      flexShrink: 0,
    },
    instructionNumPending: {
      background: "#FEE2E2",
      color: "#D91F05",
    },
    // Confirmation Box
    confirmationBox: {
      background: "#F0FDF4",
      border: "2px solid #22C55E",
      borderRadius: 12,
      padding: 32,
      textAlign: "center" as const,
      margin: "16px 0",
    },
    confirmIcon: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      background: "#DCFCE7",
      border: "2px solid #22C55E",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 12px",
      color: "#22C55E",
      fontSize: 28,
    },
    confirmTitle: {
      fontSize: 18,
      fontWeight: 700,
      color: "#16A34A",
      marginBottom: 8,
    },
    confirmText: {
      fontSize: 13,
      color: "#4B5563",
      marginBottom: 16,
    },
    valorPagoCard: {
      display: "inline-block",
      border: "2px solid #22C55E",
      borderRadius: 10,
      padding: "12px 24px",
    },
    valorPagoLabel: {
      fontSize: 10,
      fontWeight: 600,
      color: "#6B7280",
      textTransform: "uppercase" as const,
      letterSpacing: 0.5,
    },
    valorPagoAmount: {
      fontSize: 28,
      fontWeight: 700,
      color: "#1F2937",
    },
    // Payment Details
    paymentDetails: {
      borderTop: "1px solid #E5E7EB",
      marginTop: 16,
      paddingTop: 16,
    },
    paymentRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: "1px solid #F3F4F6",
    },
    paymentRowLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: "#6B7280",
      fontWeight: 500,
    },
    paymentRowValue: {
      fontSize: 12,
      fontWeight: 600,
      color: "#1F2937",
    },
    paymentRowValueHighlight: {
      color: "#D91F05",
    },
    paymentRowValueHighlightGreen: {
      color: "#16A34A",
    },
    // Footer
    footer: {
      padding: "16px 20px",
      paddingBottom: 24,
      background: "#fff",
      borderTop: "1px solid #E5E7EB",
      display: "flex",
      gap: 12,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: "12px 16px",
        paddingBottom: "calc(16px + env(safe-area-inset-bottom, 8px))",
        gap: 8,
        flexWrap: "wrap" as const,
      },
    },
    btnOutline: {
      flex: 1,
      padding: 10,
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      textAlign: "center" as const,
      background: "transparent",
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      [theme.breakpoints.down("xs")]: {
        padding: 8,
        fontSize: 12,
      },
    },
    btnOutlineRed: {
      border: "2px solid #D91F05",
      color: "#D91F05",
      "&:hover": {
        background: "#FEE2E2",
      },
    },
    btnOutlineGreen: {
      border: "2px solid #22C55E",
      color: "#22C55E",
      "&:hover": {
        background: "#DCFCE7",
      },
    },
    btnSecondary: {
      flex: 1,
      padding: 10,
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      textAlign: "center" as const,
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      color: "#374151",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      fontFamily: "Montserrat, Arial, sans-serif",
      transition: "background 150ms ease",
      "&:hover": {
        background: "#F3F4F6",
      },
      "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
      },
      [theme.breakpoints.down("xs")]: {
        padding: 8,
        fontSize: 12,
        gap: 4,
      },
    },
    // Loading
    loadingContainer: {
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
      minHeight: 200,
      width: "100%",
      position: "relative" as const,
      padding: "40px 20px",
      gap: 16,
    },
    loadingCloseButton: {
      position: "absolute" as const,
      top: 12,
      right: 12,
      width: 28,
      height: 28,
      borderRadius: "50%",
      border: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: "#fff",
      color: "#6B7280",
      fontSize: 14,
      "&:hover": {
        background: "#F3F4F6",
      },
    },
    loadingText: {
      fontSize: 13,
      fontWeight: 500,
      color: "#6B7280",
      marginTop: 4,
    },
  })
);

export default usePixModalStyles;
